// 게임 시뮬레이션 진입점. 고정 60Hz step에서 영웅 시간과 세계(몹·보스) 시간을 나눠 진행한다(COMBAT-FEEL 2.1).
// 영웅: 히트스톱·일시정지에만 멈춘다. 세계: 히트스톱·일시정지에 멈추고 각성 중 AWAKEN.worldScale 배율로 흐른다.
import { EventQueue, Ev, TeleSource, type GameEvent, type TeleSourceId } from "../shared/events";
import { FIXED_DT, HERO, JUST_DODGE, KO, MASS_SLOW, heroSkillSpecs } from "../shared/config";
import { MAPS, rasterize, cellAt, gateAt, lavaAt, villageOf, Cell, type MapDef, type MapGrid, type RegionId } from "../shared/levels";
import { gridOfMap } from "../shared/stageMaps";
import { AwakenPhase, Btn, PLAYABLE_HEROES, Scene, combatScene, type Game, type GameCommand, type GameView, type InputFrame, type PlayableHeroId } from "../shared/types";
import { Rng } from "../core/rng";
import { AwakenState, BossStateView, FlowState, HeroState, MobsState, ResultState, makeTelegraphs } from "./state";
import { SaveStore, type SaveStorage, type SaveOptions } from "./save";
import { HeroController } from "./hero";
import { Combat } from "./combat";
import { MobController } from "./mobs";
import { FlowController } from "./flow";
import { BossController } from "./boss";
import { Awakening } from "./awaken";
import { Rampage, RampageState } from "./rampage";
import { deriveStats, makeStats, npcDef } from "../shared/rpg";
import { townGearBuyableBy, townGearOffer } from "../shared/content/townShop";
import { legacyPlan } from "../shared/content/legacyGear";
import { REGION_MOB_LEVEL, adjustedXp, growthMods, makeGrowthMods, type GrowthMods } from "../shared/growth";
import { HERO_SKILL_TABLE } from "../shared/skills/types";
import { SkillSlotStore } from "./skillSlots";
import { nextSkillLevel, skillBit, skillsLearnedBetween } from "../shared/skills/unlock";
import { ProfileStore, type ProfileOptions } from "./profile";
import { GrowthStore, type GrowthCommand } from "./growth";
import { SkillEffects } from "./skillEffects";
import { Arrows } from "./arrows";
import { HeroSelectStore } from "./heroSelect";
import { BASE_TEMPO, REGIONS, type MobTempo } from "../shared/regions";
import { Hazards, ThreatClock } from "./hazards";
import { VillageController } from "./village";
import { GearStore } from "./gear";
import { LootController } from "./loot";
import { SpecialController } from "./special";
import { StageProgressStore } from "./progress";
import { HardProgressStore } from "./hardProgress";
import { HellBrand } from "./hellBrand";
import { ENHANCE_MAX_UNLOCK_REGION, STAGES_PER_REGION, enhanceCap } from "../shared/content/world";
import type { AnyStageId } from "../shared/content/types";
import { weekIndex } from "../shared/content/loot";
import type { GearCode, GearOp } from "../shared/gearLedger";
import { gearTotalsOf, type GearCmdKind } from "../shared/gearView";
import { applyElixirs, gearDamageTakenMult, makeGearTotals, type GearTotals } from "../shared/gear";
import { ConsumableController } from "./consumables";
import { RaidController } from "./raid";
import { RaidStore } from "./raidStore";
import { DailyStore } from "./daily";
import { DailyRun } from "./dailyRun";
import { TrialRun } from "./trialRun";
import { JobRun } from "./jobRun";
import { settleDaily } from "./dailyPay";
import { AvatarStore } from "./avatarStore";
import { AdventureStore } from "./adventureStore";
import { CODEX_AURA_AVATAR, CODEX_GEM_EACH, bountyFor, codexCount, codexOf, codexRewardOf } from "../shared/adventure";
import { namedFor } from "../shared/content/items";
import { applyAvatarTotals, craftCheck, craftRecipe, rollAvatarDrop, type AvatarCraftCode, type AvatarSlot } from "../shared/avatar";
import { hashSeed, nextRand, seedRng } from "../shared/gear";
import { stageById } from "../shared/content/world";
import { hardUnlocked, isRaidId, raidDef, raidStageKey } from "../shared/content/raid";
import { ELIXIRS, GEM_CODES, GEM_COLORS, JEWELER_TOWN, ALCHEMIST_TOWN, gemCode } from "../shared/content/consumables";
import { RebirthController } from "./rebirth";
import { StatController } from "./statAlloc";
import { MasteryController } from "./mastery";
import { altHeroXpMult } from "../shared/field/relevel";
import { LEVEL_MAX } from "../shared/growth";
import { rebirthSlotBonus } from "../shared/rebirth";
import { FieldController } from "./field/controller";
import { PartyLink } from "./partyLink";
import { RaidNetLink } from "./raidLink";
import { RAID_FRAMES, pinQaRaidFrame, type QaRaidFrame } from "./qaFrame";

/** 세계 배율 고정소수 단위. 0.2를 다섯 번 더하면 정확히 1틱이 되도록 정수로 누산한다. */
const RATE_UNIT = 1000;
type QaOp = Extract<GameCommand, { type: "qa" }>["op"];

export class GameSim implements Game {
  readonly threats = new ThreatClock();
  readonly hazards = new Hazards(this);
  readonly events = new EventQueue(2048);
  readonly rng: Rng;
  readonly save: SaveStore;
  readonly hero = new HeroState();
  readonly mobs = new MobsState();
  readonly boss = new BossStateView();
  readonly telegraphs = makeTelegraphs();
  readonly flow = new FlowState();
  readonly awaken = new AwakenState();
  readonly combo = { count: 0, timer: 0, best: 0, praiseTier: 0 };
  /** 스킬 연계(LINK). heroController가 쓰고 combat이 배율을 읽는다 */
  readonly link = { stage: 0, timer: 0, used: 0, cancelReady: 0, finishes: 0, best: 0 };
  /** 공격 자석 대상(heroController가 쓰고 view.magnet으로 노출) */
  readonly magnet = { target: -1, boss: false };
  /** 연속 처치(폭주) 상태. rampageController가 쓰고 view.rampage로 노출 */
  readonly rampage = new RampageState();
  readonly result = new ResultState();
  readonly settings = { mature: false, reduceFlash: false, skipCutin: false };
  readonly stats = makeStats();
  readonly village = new VillageController(this);
  readonly profile: ProfileStore;
  /**
   * 영웅별 성장 장부(c022): 카엘 = growth.v2(옛 키 그대로), 라이라 = growth.lyra.v1. 판 EXP는 그 판을 플레이한 영웅 장부로 간다(던전 안 전환 금지)
   */
  readonly growthByHero: Record<PlayableHeroId, GrowthStore>;
  /** 환생(레인 R, dungeonhero.rebirth.v1): 영웅별 회차·환생 포인트·윤회의 문. 성장 장부는 회차별 키(game/rebirth growthKeyOf) */
  readonly rebirth: RebirthController;
  /** 자유 스탯 배분(레인 C, dungeonhero.stats.v1): 영웅·환생 회차별. 효과는 growthMods 직후 gmods에 더한다 */
  readonly statAlloc: StatController;
  /** 숙련·초월(C4): 성장 원장 넘친 EXP → 숙련, 배분·초월은 장비 장부 */
  readonly mastery: MasteryController;
  /** 환생 배분 + 숙련·초월 합(deriveStats 입력, 재사용) */
  private readonly powerIn = { atkPct: 0, hpPct: 0, crit: 0 };
  /** 영웅별 스킬 슬롯 6×2 배치(c022): 카엘 = skillslots.v1, 라이라 = skillslots.lyra.v1 */
  readonly slotsByHero: Readonly<Record<PlayableHeroId, SkillSlotStore>>;
  /** 선택한 영웅(dungeonhero.hero.v1, 마을에서만 바뀐다) */
  readonly heroSelect: HeroSelectStore;
  /** 지금 플레이하는 영웅 */
  get heroId(): PlayableHeroId { return this.heroSelect.hero; }
  /** 다른 영웅 계정 보너스 배율(C12, shared/field/relevel.ts altHeroXpMult). 레벨 99·환생 여부만 본다 */
  get altXpMult(): number {
    const me = this.heroId;
    let maxed = false;
    for (const id of PLAYABLE_HEROES) if (id !== me && (this.rebirth.cycleOf(id) >= 1 || (this.growthByHero[id]?.level ?? 0) >= LEVEL_MAX)) { maxed = true; break; }
    return altHeroXpMult(this.rebirth.cycleOf(me), maxed);
  }
  /** 성장 v2(레벨·EXP·스킬 트리 랭크) — 지금 영웅의 장부. 저장은 판 끝과 마을 복귀·배우기에서만 한다 */
  get growth(): GrowthStore { return this.growthByHero[this.heroSelect.hero]; }
  /** 스킬 슬롯 6×2 배치·페이지(입력층·HUD용. 시뮬레이션 상태가 아니다) — 지금 영웅의 배치 */
  get slots(): SkillSlotStore { return this.slotsByHero[this.heroSelect.hero]; }
  /** 장비 6부위·가방·강화(D2a·D2b, dungeonhero.gear.v1). 줍기는 메모리에 바로, 저장은 flushSaves·판 끝·마을 명령에서 */
  readonly gear: GearStore;
  /** 바닥 드롭 풀·줍기(D2a) */
  readonly loot: LootController;
  /** 스페셜 몬스터(황금 고블린·챔피언, STAGE-WORLD-PRD 4절) */
  readonly special: SpecialController;
  /** 스테이지 진행(D3a, dungeonhero.stage.v1): 1-1 … 4-10 순차 해금·지금 마을 */
  readonly progress: StageProgressStore;
  /** 하드 난이도 진행(D3b, dungeonhero.stage.hard.v1 — 새 키만) */
  readonly hard: HardProgressStore;
  /** 헬 난이도 진행(C10, dungeonhero.stage.hell.v1 — 새 키만). 노말 전부 + 하드 n-10이 헬 n을 연다 */
  readonly hell: HardProgressStore;
  /** 헬 보스 판 「지옥불 각인」(C10) */
  readonly hellBrand: HellBrand;
  /** 물약 퀵슬롯·자동 물약·부활 깃털·비약 끝 알림(D2c). 보유 수·변경은 장비 장부 */
  readonly consumables: ConsumableController;
  /** 레이드(RAID-PRD): 비룡 두뇌·용병 3·불 장판·위협 */
  readonly raid: RaidController;
  /** 레이드 보상 정산 표시(dungeonhero.raid.v1) */
  readonly raidStore: RaidStore;
  /** 일일 콘텐츠 장부(D3c, dungeonhero.daily.v1). 의뢰 수령·던전·탑 보상은 여기 적고 dailyPay.settleDaily가 지갑에 넣는다(L9) */
  readonly daily: DailyStore;
  /** 일일 던전·무한의 탑 한 판 흐름(L9a) */
  readonly dailyRun: DailyRun;
  /** 용사의 시련 한 판(3개월 C7): 경험치 없는 도전 판. 이룬 기록 = save.v1 보너스 영수증 `trial:<id>` */
  readonly trialRun: TrialRun;
  /** 전직 퀘스트(3개월 C10-5): 전직 단계·갈래 = save.v1 고정 id 영수증 `job:<영웅>:…`, 관문 판은 trialRun이 돌린다 */
  readonly jobRun: JobRun;
  /** 아바타 장부(레인 E, dungeonhero.avatar.v1): 외형·작은 능력치(상한)·세트 */
  readonly avatar: AvatarStore;
  /** 모험 목표판 장부(레인 E 2단계, dungeonhero.adventure.v1): 현상금 완료·도감 */
  readonly adventure: AdventureStore;
  /** 아바타 보스 드롭 씨앗(장비·보석·유니크 수열과 별개, AVATAR-ADVENTURE-PRD 1.6). 판마다 판 고유값과 섞어 다시 시드한다 */
  private readonly avatarSeed: number;
  /** 마지막으로 잔액에 반영한 장비 장부 revision */
  private gearRevision = -1;
  /** 잔액 계산에 마지막으로 쓴 장비 순지출(NaN = 아직 없음) */
  private gearGoldNet = Number.NaN;
  private avatarOwedRev = -1;
  /** 장비 통합 전환(ECONOMY 14절): 끝났는가 */
  private legacyDone = false;
  /**
   * 다음 시도까지 남은 틱. 끝나지 못한 시도 뒤에는 장부·프로필 리비전과 무관하게 2초(120틱)를 기다린다
   * (쓰기 실패가 리비전을 올려 매 틱 다시 쓰던 것, PR #205 Codex P2)
   */
  private legacyWait = 0;
  /** 마지막으로 이벤트를 낸 장비 알림 순번 */
  private gearNoticeSeq = 0;
  /**
   * 전투 능력치에 쓰는 장비 합계 사본. 던전 밖(마을·입장·재도전 직전)에서만 장비 장부 값으로 맞추고, 던전 안에서는 입장 때 값을 쓴다
   * (ECONOMY.md "던전 입장·재도전 때 실제 전투 능력치로 고정" — 다른 탭 장착 변화가 던전 레벨업 재계산에 섞이지 않게, 교차 리뷰 P2)
   */
  private readonly statsGear: GearTotals = makeGearTotals();
  private statsGearRev = -1;
  /**
   * 능력치 사본에 들어 있는 비약 비트(D2c). 마을에서는 지금 켜진 비약 그대로, 던전에서는 입장 때 켜져 있던 것 + 판 도중 새로 마신 것
   * (판 도중 시간이 끝난 비약도 판 끝까지 유지 — 입장 때 고정 규칙과 같은 결)
   */
  private statsBuffMask = 0;
  /** 전투·드롭이 쓰는 장비 + 비약 합계(입장 때 고정 사본, 읽기 전용) */
  get buffedTotals(): Readonly<GearTotals> { return this.statsGear; }
  /**
   * 자유 입장 허용(지역만 주는 enterDungeon = 지도 그대로·진행 기록 없음). 시험·QA 하네스 기본 true,
   * 실제 빌드(main.ts, ?qa·개발 서버가 아닐 때)는 false: 지역 입장 명령도 그 지역의 고른 스테이지로 들어가고 잠긴 지역은 거절한다(PR #160 리뷰 P2)
   */
  allowFreeEntry = true;
  /** 장비 명령·장부 결과에 쓰는 벽시계(주간 보호 주문서 한도). 테스트가 바꾼다 */
  wallClock: () => number = Date.now;
  /** 마지막으로 전투 보정을 맞춘 성장 스냅샷 번호 */
  private growthEpoch = -1;
  /** 트리 보정값. 로드·배우기·초기화·입장·레벨업 때만 다시 계산한다(프레임마다 아님) */
  readonly gmods: GrowthMods = makeGrowthMods();
  /** 진홍 초승달 검기와 랭크 5·캡스톤 판정 */
  readonly skillFx: SkillEffects;
  /** 라이라 화살·장판(c022) */
  readonly arrows: Arrows;
  /** 마지막 능력치 계산에 쓴 주력 스킬(레벨업 때 같은 값으로 다시 계산한다) */
  private statsFocus = 0;
  private statsRevision = -1;
  readonly grids = { village: rasterize(MAPS.village), r1: rasterize(MAPS.r1), r2: rasterize(MAPS.r2), r3: rasterize(MAPS.r3), r4: rasterize(MAPS.r4), r5: rasterize(MAPS.r5), r6: rasterize(MAPS.r6) };
  readonly heroController: HeroController;
  readonly combat: Combat;
  readonly mobController: MobController;
  readonly flowController: FlowController;
  readonly bossController: BossController;
  readonly awakening: Awakening;
  readonly rampageController: Rampage;
  /** ARPG 필드·균열(레인 F, REBIRTH-MODES-SPEC 3장) */
  readonly field: FieldController;
  /** 파티 판 연결점(레인 D, docs/PARTY-TRADE-PRD.md 3장). 연결층(PartyClient)은 UI 쪽이 attach한다 */
  readonly party: PartyLink;
  /** 레이드 4인 넷 판 연결점(docs/RAID-PRD.md 0장 2단계). 연결층(RaidClient)은 main.ts가 attach한다 */
  readonly raidNet: RaidNetLink;
  readonly view: GameView;
  map: MapDef = MAPS.village;
  grid: MapGrid = this.grids.village;
  tick = 0;
  hitstop = 0;
  /** 결투장식 KO 슬로가 남은 영웅 틱(그동안 세계만 KO.worldScale배) */
  koSlow = 0;
  /** 저스트 회피 세계 슬로 잔여 영웅 틱 */
  justDodgeSlow = 0;
  /** 대량 처치 세계 슬로 잔여 틱(그동안 세계만 MASS_SLOW.worldScale배, COMBAT-FEEL 2.14) */
  massSlow = 0;
  private dodgeAge = -1;
  private dodgeHit = false;
  private justDodgeCounter = 0;
  /** 마지막 KO를 건 틱(같은 틱에 엘리트이자 방 마지막 몹이면 한 번만) */
  private koTick = -1;
  /** 직전 step에서 영웅·세계가 실제로 진행한 틱(0..1) */
  heroRate = 0;
  worldRate = 0;
  /** 이번 step에서 세계 정수 타이머가 1틱 넘어갔는지 */
  worldTick = false;
  /** 세계 시계의 소수 부분(0..1). 정수 타이머 + 이 값 = 연속 경과 틱 */
  worldFrac = 0;
  /** QA 픽스처 명령 실행 횟수. 정상 플레이 증거와 구분하는 기록이다 */
  qaOps = 0;
  /** C8-6 레이드 룩 고정 프레임(?qa 전용, game/qaFrame.ts). 켜져 있으면 장면 상태를 매 틱 다시 쓰고 시뮬을 멈춘다 */
  qaFrame: QaRaidFrame | null = null;
  private readonly qaFrameStage = { armed: 0 };
  /** 고정 프레임 켜기·끄기(이름은 RAID_FRAMES 키). 켤 때 qaOps에 센다. 모르는 이름이면 false */
  setQaFrame(name: string | null): boolean {
    if (name === null) { this.qaFrame = null; this.qaFrameStage.armed = 0; return true; }
    const f = RAID_FRAMES[name];
    if (!f) return false;
    this.qaFrame = f; this.qaOps++;
    // 이미 고정 중에 다른 장면으로 바꾸면 다음 틱에 다시 '첫 고정'으로 본다(장면 번호를 올려 VFX·카메라를 새로 시작)
    if (this.qaFrameStage.armed === 3) this.qaFrameStage.armed = 2;
    return true;
  }
  /** 누적 영웅 시간(틱). 매 step 끝에 heroRate를 더한다 */
  heroTime = 0;
  /** 누적 세계 시간 단위(RATE_UNIT 배수, 부동소수 누적 오차 방지) */
  private worldTimeUnits = 0;
  private worldAcc = 0;
  private readonly pending = new Array<GameCommand | null>(32).fill(null);
  private pendingCount = 0;
  private bufferedButtons = 0;
  private applyingCommand = false;
  private menuInputBlocked = false;
  private readonly frame: InputFrame = { moveX: 0, moveZ: 0, held: 0, pressed: 0 };

  constructor(seed = 1, storage?: SaveStorage, profileOptions?: ProfileOptions, saveOptions?: SaveOptions, gearOptions?: { clock?: () => number }) {
    this.rng = new Rng(seed);
    this.save = new SaveStore(storage, saveOptions);
    // 골드 잔액 = 획득 + 준비금 − 상점 구매 − 장비 순지출(저장 확인된 값 + 미확인 지출, 미확인 수입 제외). 장비 장부는 아래에서 만든다
    this.profile = new ProfileStore(this.save, storage, { ...profileOptions, gearNet: () => (this.gear ? this.gear.committedGoldNet : 0) + this.owedGold() });
    const store = storage ?? this.save.backingStorage;
    this.heroSelect = new HeroSelectStore(store);
    this.rebirth = new RebirthController(this, store);
    this.statAlloc = new StatController(this, store);
    this.growthByHero = { kael: this.rebirth.makeGrowth(store, "kael"), lyra: this.rebirth.makeGrowth(store, "lyra") };
    this.gear = new GearStore(storage ?? this.save.backingStorage, { clock: gearOptions?.clock });
    this.mastery = new MasteryController(this, store);
    // 진행은 save.v1 지역 기록에서 한 번 이관한다(키가 없을 때). 장비 강화 상한·보호 주문서 해금이 이 값(도착한 마을)을 쓴다
    this.progress = new StageProgressStore(storage ?? this.save.backingStorage, () => this.save.records, () => this.save.stageEvidence());
    this.hard = new HardProgressStore(storage ?? this.save.backingStorage, () => this.progress.cleared);
    this.hellBrand = new HellBrand(this);
    this.hell = new HardProgressStore(storage ?? this.save.backingStorage, () => this.progress.cleared, "hell", () => this.hard.cleared);
    this.loot = new LootController(this, seed);
    this.special = new SpecialController(this, seed);
    this.consumables = new ConsumableController(this);
    this.raid = new RaidController(this, seed);
    this.raidStore = new RaidStore(storage ?? this.save.backingStorage);
    // 정산 표시(raid.v1)가 확인된 레이드 영수증만 save.v1 영수증 접기(D10)가 지울 수 있다
    this.save.raidSettled = (id) => this.raidStore.isClaimed(id);
    this.daily = new DailyStore(() => this.wallClock(), store);
    this.avatar = new AvatarStore(store);
    this.adventure = new AdventureStore(store);
    this.avatarSeed = seed;
    this.profile.recompute();
    this.slotsByHero = { kael: new SkillSlotStore(store), lyra: new SkillSlotStore(store, { hero: "lyra" }) };
    // 환생 1차부터 스킬 슬롯 +1(페이지마다 7번째 칸)
    for (const id of PLAYABLE_HEROES) this.slotsByHero[id].setExtra(rebirthSlotBonus(this.rebirth.cycleOf(id)));
    this.slots.sync(this.growth);
    this.heroController = new HeroController(this);
    this.skillFx = new SkillEffects(this);
    this.combat = new Combat(this);
    this.arrows = new Arrows(this);
    this.mobController = new MobController(this);
    this.flowController = new FlowController(this);
    this.dailyRun = new DailyRun(this);
    this.trialRun = new TrialRun(this);
    this.jobRun = new JobRun(this);
    this.bossController = new BossController(this);
    this.awakening = new Awakening(this);
    this.rampageController = new Rampage(this);
    this.field = new FieldController(this, seed, storage ?? this.save.backingStorage);
    this.party = new PartyLink(this);
    this.raidNet = new RaidNetLink(this);
    const self = this;
    this.view = {
      get tick() { return self.tick; },
      get timeScale() { return self.timeScale; },
      get hitstop() { return self.hitstop; },
      get heroRate() { return self.heroRate; },
      get worldRate() { return self.worldRate; },
      get heroTime() { return self.heroTime; },
      get worldTime() { return self.worldTimeUnits / RATE_UNIT; },
      get qaFrame() { return self.qaFrame && self.qaFrameStage.armed === 3 ? self.qaFrame : null; },
      get map() { return self.map; },
      get grid() { return self.grid; },
      hero: this.hero, mobs: this.mobs, boss: this.boss, telegraphs: this.telegraphs, flow: this.flow,
      combo: this.combo, link: this.link, magnet: this.magnet, rampage: this.rampage, awaken: this.awaken, result: this.result, settings: this.settings,
      profile: this.profile, get growth() { return self.growth; }, get slots() { return self.slots; }, get heroId() { return self.heroId; }, stats: this.stats, village: this.village, events: this.events,
      records: this.save.records, progress: this.progress, hard: this.hard, hell: this.hell, hellBrand: this.hellBrand.view, gear: this.gear, loot: this.loot.state, consume: this.consumables.state, special: this.special.state, field: this.field.view,
      raid: this.raid.state,
      trials: this.trialRun.view,
      job: this.jobRun.view,
      rebirth: this.rebirth.view,
      party: this.party.view,
      raidNet: this.raidNet.view,
      statAlloc: this.statAlloc.view,
      mastery: this.mastery.view,
      avatar: this.avatar,
      adventure: { get revision() { return self.adventure.revision; }, get done() { return self.adventure.done; }, get found() { return self.adventure.found; },
        get day() { return self.adventure.dayAt(self.wallClock()); }, get highestTown() { return self.highestTown(); },
        get claimed() { return self.adventure.claimed; }, get lastClaim() { return self.adventure.lastClaim; } },
    };
    this.flow.town = this.progress.town;
    this.map = villageOf(this.flow.town);
    // 마을②~④(S7)는 지도마다 판정 격자가 따로다
    if (this.map !== MAPS.village) this.grid = gridOfMap(this.map);
    this.flow.stageLabel = this.map.name;
    this.snapHero(this.map.spawn.x, this.map.spawn.z);
  }

  /** 몹·보스 시간 배율(각성 단계표, KO 슬로 중이면 더 느린 쪽) */
  get timeScale(): number {
    const s = this.awakening.worldScale();
    let scale = s;
    if (this.koSlow > 0) scale = Math.min(scale, KO.worldScale);
    if (this.justDodgeSlow > 0) scale = Math.min(scale, JUST_DODGE.worldScale);
    if (this.massSlow > 0) scale = Math.min(scale, MASS_SLOW.worldScale);
    return scale;
  }

  /** 각성 게이지 획득 배율 = 스킬 트리 × 장비 옵션·세트(던전 입장 때 고정된 능력치) */
  awakenMult(): number { return this.gmods.awakenGainMult * this.stats.awakenGainMult; }
  /** 장비 방어(몹 레벨 곡선)·받는 피해 감소 옵션·세트를 합친 받는 피해 배율(BUFF_CAP 50%까지) */
  gearDamageTakenMult(): number {
    if (this.stats.def <= 0 && this.stats.damageTakenMult >= 1) return 1;
    return gearDamageTakenMult(this.stats.def, this.stats.damageTakenMult, this.mobLevel);
  }
  /** 지금 판 몹 레벨(레벨 차 EXP 보정·방어 감소 기준): 판 계획 값(노말 = 지역 구간 끝, 하드 +10). 마을은 지역1 값 */
  get mobLevel(): number { return this.map.id === "village" ? REGION_MOB_LEVEL[this.regionKey] : this.flowController.plan.mobLevel; }

  beginDodgeWindow(): void { this.dodgeAge = 0; this.dodgeHit = false; }
  tickJustDodgeCounter(): void {
    if (this.dodgeAge >= 0) {
      this.dodgeAge++;
      if (this.dodgeAge > JUST_DODGE.window) this.dodgeAge = -1;
    }
    if (this.justDodgeCounter > 0) this.justDodgeCounter--;
  }
  hasJustDodgeCounter(): boolean { return this.justDodgeCounter > 0 && this.awaken.phase === AwakenPhase.None && combatScene(this.flow.scene); }
  consumeJustDodgeCounter(): boolean {
    if (!this.hasJustDodgeCounter()) return false;
    this.justDodgeCounter = 0;
    return true;
  }
  clearJustDodgeCounter(): void { this.justDodgeCounter = 0; }
  triggerJustDodge(source: number): void {
    if (this.dodgeAge < 0 || this.dodgeHit || this.awaken.phase !== AwakenPhase.None || !combatScene(this.flow.scene)) return;
    this.dodgeHit = true;
    this.dodgeAge = -1;
    this.justDodgeSlow = Math.max(this.justDodgeSlow, JUST_DODGE.slowTicks);
    this.justDodgeCounter = JUST_DODGE.counterTicks;
    this.hero.awakenGauge = Math.min(1, this.hero.awakenGauge + JUST_DODGE.awakenGain * this.awakenMult());
    const e = this.emit(Ev.JustDodge);
    e.x = this.hero.x; e.z = this.hero.z; e.a = JUST_DODGE.slowTicks; e.b = source;
  }

  command(cmd: GameCommand): void {
    if (this.pendingCount === this.pending.length) throw new Error("게임 명령 큐가 가득 찼습니다");
    this.pending[this.pendingCount++] = cmd;
  }

  step(dt: number, input: InputFrame): void {
    if (dt !== FIXED_DT) throw new Error("step(dt)는 FIXED_DT만 받는다");
    const qf = this.qaFrame;
    if (qf && pinQaRaidFrame(this, qf, this.qaFrameStage)) {
      // 고정 프레임: 시뮬·사건 없이 상태만 다시 쓰고, 렌더 시계를 장면 값에 묶는다(맥동·반복 클립이 같은 시각)
      this.heroRate = 0; this.worldRate = 0; this.hitstop = 0;
      this.heroTime = qf.heroTime; this.worldTimeUnits = Math.round(qf.worldTime * RATE_UNIT);
      return;
    }
    this.stepInner(input);
    // 렌더 시계용 누적 시간: 한 프레임에 여러 틱이 지나도 틱별 배율이 정확히 쌓인다.
    this.heroTime += this.heroRate;
    this.worldTimeUnits += Math.round(this.worldRate * RATE_UNIT);
  }

  private stepInner(input: InputFrame): void {
    this.flowController.tickSave();
    this.gear.tick();
    this.gear.setHero(this.heroId); // 영웅별 무기 칸 보기(같으면 아무것도 안 함)
    this.profile.tick();
    // 쓰기 잠금을 넘겨받는 순간(다른 탭이 닫힘 등) 아바타 장부를 다시 읽는다: 그 탭이 남긴 미정산 owed를 예약·정산하게(PR #262 Codex P2)
    if (this.profile.mode === "ok" && this.lastProfileMode !== "ok") { this.avatar.read(); this.owedBooted = false; }
    this.lastProfileMode = this.profile.mode;
    this.syncGear();
    // 끊김으로 남은 제작 골드 결제 정산: 첫 틱과 약 2초마다(미정산이 있을 때만)
    if (this.avatar.owed.length > 0 && (!this.owedBooted || ++this.owedTimer >= 120)) { this.settleAvatarOwed(); this.owedTimer = 0; }
    this.owedBooted = true;
    if (!this.legacyDone) this.unifyLegacyGear();
    // 영웅별 장부·배치 모두 재시도를 센다(지금 영웅이 아닌 장부의 모호한 쓰기도 확인을 이어간다)
    for (const id of PLAYABLE_HEROES) { this.growthByHero[id].tick(); this.slotsByHero[id].tick(); }
    // 저장 잠금이 풀리며 성장 스냅샷이 바뀌었으면(판 기록·재시도 중 복구) 전투 보정도 바로 맞춘다(PR #145 Codex 8차)
    if (this.growth.snapshotEpoch !== this.growthEpoch) this.syncGrowth();
    const menuAtStart = this.village.open;
    this.menuInputBlocked = menuAtStart;
    this.applyingCommand = true;
    for (let i = 0; i < this.pendingCount; i++) { this.apply(this.pending[i]!); this.pending[i] = null; }
    this.pendingCount = 0;
    this.syncGear();
    if (this.flow.scene === Scene.Village && this.statsRevision !== this.profile.revision) this.refreshStats();
    // 명령의 순간이동 뒤에 prev를 맞춰, 이번 틱 보간이 순간이동을 가로지르지 않게 한다.
    this.copyPrevious();
    this.heroRate = 0; this.worldRate = 0; this.worldTick = false;
    // 일시정지는 마을·던전에서만 유효하다(타이틀·결과·패배 화면에 보이지 않는 일시정지가 걸리지 않게).
    const menuBeforeInput = this.village.open;
    const menuPause = (menuAtStart || menuBeforeInput) && (input.pressed & Btn.Pause) !== 0;
    if (menuPause) this.village.closeMenu();
    if (!this.pausable()) this.flow.paused = false;
    else if ((input.pressed & Btn.Pause) && !menuPause) this.flow.paused = !this.flow.paused;
    if (!menuPause && !menuAtStart) this.village.interact(input);
    // 닫는 틱까지 입력을 버린다. 히트스톱·영웅 선입력 버퍼에도 남기지 않는다.
    // 마을 이동 연출(D3a) 동안에도 영웅 입력을 막는다
    const blockInput = this.menuInputBlocked || menuAtStart || menuBeforeInput || this.village.open || this.flow.travelTo > 0 || this.field.mapOpen;
    this.applyingCommand = false;
    if (blockInput) this.bufferedButtons = 0;
    // 파티 판(레인 D): 서버 사건(판 시작·몹 등장·예고·판정·처치·끝)을 받아 판에 적용하고 내 위치를 보낸다. 일시정지가 없다(같은 판을 둘이 본다)
    this.party.step();
    this.raidNet.step();
    if (this.flow.paused) return;
    this.tick++;
    // 클리어 연출 대기는 실제 틱으로 센다(히트스톱·KO 슬로 동안에도 흐른다)
    if (this.flow.clearHold > 0) this.flowController.tickClearHold();
    // 히트스톱 동안 눌린 버튼은 버퍼에 남겨 정지가 끝난 틱에 전달한다.
    if (!blockInput) this.bufferedButtons |= input.pressed & ~Btn.Pause;
    // 히트스톱 길이 N틱: 영웅은 N틱 멈춘다. 세계(몹·보스)는 타격이 난 틱에도 진행하지 않으므로 N+1틱 멈춘다.
    // 맞은 몹이 타격 틱에 한 걸음 더 밀려 보이지 않게 하려는 의도된 차이다(±1프레임 측정 기준 안).
    // 시련 무혈 판정: 피해 뒤 히트스톱·물약 회복보다 먼저 최저 HP를 잰다(PR #293 Codex P2)
    this.trialRun.sampleHp();
    if (this.hitstop > 0) { this.hitstop--; return; }
    const f = this.frame;
    f.moveX = !blockInput && Number.isFinite(input.moveX) ? input.moveX : 0;
    f.moveZ = !blockInput && Number.isFinite(input.moveZ) ? input.moveZ : 0;
    const length = Math.hypot(f.moveX, f.moveZ);
    if (length > 1) { f.moveX /= length; f.moveZ /= length; }
    f.held = blockInput ? 0 : input.held; f.pressed = this.bufferedButtons; this.bufferedButtons = 0;
    this.flow.sceneTick++;
    const scene = this.flow.scene;
    if (scene === Scene.Title) { if (f.pressed & Btn.Interact) this.flowController.loadMap("village"); return; }
    if (scene !== Scene.Village && !combatScene(scene)) return;
    if (combatScene(scene)) this.flow.elapsedTicks++;
    this.heroRate = 1;
    this.combat.step();
    this.rampageController.step();
    // 검기·지연 판정은 영웅 동작보다 먼저 진행한다(예약한 틱에 정확히 발동하도록)
    this.skillFx.step();
    this.arrows.step();
    this.heroController.step(f);
    this.trialRun.sampleHp();
    // 물약 퀵슬롯·자동 물약(던전)·플레이 시계와 비약 끝(마을·던전), D2c
    this.consumables.step(f);
    if (combatScene(scene)) this.loot.step();
    else this.flowController.tickTravel();
    this.hazards.status();
    this.hazards.burn();
    this.village.updateNear();
    // 이번 틱에 타격이 나면 세계는 이 틱부터 멈춘다(히트스톱 중 몹 prev === 현재).
    if (!this.playing() || this.hitstop > 0) return;
    this.advanceWorld();
    if (this.playing() && this.hitstop === 0) { this.flowController.step(); this.field.step(); }
  }

  private playing(): boolean {
    const s = this.flow.scene;
    // 파티 판에서는 내 영웅이 쓰러져도(부활 대기) 서버 몹·동료가 계속 움직인다
    return (s === Scene.Village || combatScene(s)) && (this.hero.hp > 0 || this.party.active || this.raidNet.active);
  }

  private pausable(): boolean {
    const s = this.flow.scene;
    if (this.party.active || this.raidNet.active) return false;
    return s === Scene.Village || combatScene(s);
  }

  /** 세계 시간 진행: 위치는 dt×배율로 매 틱 적분, 정수 타이머는 누산기가 1틱을 넘을 때만 올린다. */
  private advanceWorld(): void {
    const units = Math.round(this.timeScale * RATE_UNIT);
    // KO 슬로는 세계가 실제로 진행한 틱만 센다(히트스톱·일시정지 동안은 줄지 않는다)
    if (this.koSlow > 0) this.koSlow--;
    if (this.justDodgeSlow > 0) this.justDodgeSlow--;
    if (this.massSlow > 0) this.massSlow--;
    this.worldRate = units / RATE_UNIT;
    if (units <= 0) return;
    if (units >= RATE_UNIT) {
      // 배율 1: 매 틱이 정수 틱이다. 각성 뒤 남은 소수 부분을 버려 경고 데칼 진행이 1틱 앞서지 않게 한다.
      this.worldAcc = 0; this.worldTick = true;
    } else {
      this.worldAcc += units;
      if (this.worldAcc >= RATE_UNIT) { this.worldAcc -= RATE_UNIT; this.worldTick = true; }
    }
    this.worldFrac = this.worldAcc / RATE_UNIT;
    if (this.map.threatClock && this.worldTick) this.threats.now++;
    this.mobController.step(this.worldRate, this.worldTick);
    this.special.step();
    if (this.hitstop === 0 && combatScene(this.flow.scene) && this.hero.hp > 0) this.bossController.step(this.worldRate, this.worldTick);
    // 레이드 용병·불 장판(RAID-PRD). 보스가 쓰러뜨린 영웅 뒤(패배 화면)에는 멈춘다
    if (this.raid.active && this.hitstop === 0 && this.flow.scene === Scene.Dungeon && this.hero.hp > 0) this.raid.step(this.worldRate, this.worldTick);
    if (this.map.vents && this.playing()) this.hazards.step();
    if (this.hitstop === 0 && this.hero.hp > 0) this.hellBrand.step(this.worldTick);
  }

  private copyPrevious(): void {
    const h = this.hero, m = this.mobs, b = this.boss;
    h.prevX = h.x; h.prevY = h.y; h.prevZ = h.z;
    b.prevX = b.x; b.prevZ = b.z;
    for (let i = 0; i < m.capacity; i++) { m.prevX[i] = m.x[i]; m.prevY[i] = m.y[i]; m.prevZ[i] = m.z[i]; }
    this.raid.copyPrevious();
  }

  suppressHeroInput(): void { this.menuInputBlocked = true; this.bufferedButtons = 0; }

  /** 마을 장착 변경 또는 새 판 시작 때만 계산한다. 던전 도중 동기화는 다음 판에 적용한다. */
  refreshStats(): void {
    const p = this.profile;
    this.statsFocus = p.skillFocus;
    if (!combatScene(this.flow.scene)) {
      // 무기 칸은 영웅별(lyra-weapon-slot): 장비 보기를 지금 영웅으로 맞춘 뒤 그 영웅 합계를 쓴다(다른 영웅 전용 무기는 gearTotalsOf가 뺀다)
      this.gear.setHero(this.heroId);
      Object.assign(this.statsGear, gearTotalsOf(this.gear, this.heroId)); this.statsGearRev = this.gear.revision;
      // 레벨 동기화(레인 R): 환생 회차면 장착 장비를 지금 레벨 기준으로 다시 합친다(1회차는 그대로)
      this.rebirth.syncGear(this.statsGear);
      // 켜진 비약을 더하고 버프 합산 캡을 다시 건다(D2c). 던전 입장 직전 이 값이 판 동안 고정된다
      this.statsBuffMask = this.gear.elixirMask();
      applyElixirs(this.statsGear, this.statsBuffMask);
      // 아바타 능력치(레인 E): 능력치마다 상한(장비 옵션 기대치 15%)으로 자른 합을 더하고 버프 합산 캡을 다시 건다
      applyAvatarTotals(this.statsGear, this.avatar.totals);
      // 도감(레인 E 2단계): 가진 이름 있는 유니크를 기록한다(팔아도 도감에 남게). 새로 생긴 것이 없으면 쓰지 않는다
      const nu: string[] = [];
      for (const it of this.gear.items) if (it.nu) nu.push(it.nu);
      if (nu.length > 0) this.adventure.noteFound(nu);
    }
    this.derive(p.skillFocus);
    this.statsRevision = p.revision;
    if (this.flow.scene === Scene.Village) this.syncHeroStats(false);
  }

  /**
   * 비약 상태가 바뀌었다(마심·끝남, D2c). 마을: 능력치를 지금 상태로 다시 맞춘다. 던전: 새로 켜진 비약만 사본에 더한다
   * (판 도중 끝난 비약은 판 끝까지 유지). 이벤트 단위로만 부른다(프레임마다 아님)
   */
  refreshBuffs(): void {
    if (!combatScene(this.flow.scene)) { this.refreshStats(); return; }
    const add = this.gear.elixirMask() & ~this.statsBuffMask;
    if (add === 0) return;
    this.statsBuffMask |= add;
    applyElixirs(this.statsGear, add);
    this.derive(this.statsFocus);
    this.syncHeroStats(false);
    this.loot.setDropRate(this.statsGear.dropRate);
  }

  /**
   * 능력치 계산(영웅별, c022). 카엘 = 주력 스킬·장비 6부위 전체. 다른 영웅 = 주력 스킬(카엘 스킬 번호)을 빼고
   * 무기 칸 뺀 장비 합계(statsGear는 refreshStats가 고른다)와 그 영웅 스킬 표로 계산한다
   */
  private derive(focus: number): void {
    const kael = this.heroId === "kael";
    this.rebirth.syncStats();
    // 환생 배분 + 숙련 배분·부위 초월(C4)을 같은 % 입력으로 더한다. 숙련 재물(골드 %)은 장비 골드 배율 뒤에 더한다
    const r = this.rebirth.stats, p = this.powerIn, m = this.mastery?.stats();
    p.atkPct = r.atkPct + (m ? m.atkPct : 0); p.hpPct = r.hpPct + (m ? m.hpPct : 0); p.crit = r.crit + (m ? m.crit : 0);
    deriveStats(this.stats, kael ? focus : 0, this.gmods, this.statsGear, heroSkillSpecs(this.heroId, this.jobRun.branch), p);
    if (m) this.stats.goldMult += m.goldPct;
  }

  /**
   * 장비 통합(ECONOMY 14절): profile.v2의 옛 상점 장비를 같은 능력치의 옛 장비 개체로 gear.v1에 한 번 옮긴다.
   * 이 탭이 거래 권한을 갖고 두 장부가 정상일 때만, 장부 판본이 바뀔 때마다 한 번씩 시도한다(평소 틱은 문자열 비교 하나).
   * 순서: 장비 장부에 넣기 → profile.v2 gearUnified 기록. 두 번째가 실패해도 uid 검사로 다시 넣지 않는다.
   */
  private unifyLegacyGear(): void {
    if (this.legacyWait > 0) { this.legacyWait--; return; }
    const p = this.profile, g = this.gear;
    if (!p.writable || g.mode !== 'ok') return;
    this.legacyWait = 120;
    const uids = new Set<string>(g.items.map((it) => it.uid));
    const plan = legacyPlan({ owned: p.owned, weapon: p.weapon, armor: p.armor, done: p.gearUnified, uids,
      weaponEmpty: !g.heroWeapons.kael, armorEmpty: !g.equipped.armor });
    if (plan.add.length > 0) {
      const code = g.unifyLegacy(plan.add, plan.equip);
      if (code !== 'ok' && code !== 'duplicate') return;
    }
    // 전환 연산이 장부에 쓰이고 되읽어 확인될 때까지 gearUnified를 남기지 않는다. 확인 전(다른 탭 경합 등) 닫히면 다음 로드에 다시 전환한다
    // (미확인인데 완료 표시를 남겨 장비를 잃던 것, PR #205 Codex P1). 미기록 연산은 장부가 스스로 다시 쓴다
    if (g.legacyPending) return;
    if ([...plan.done].sort().join(',') !== [...p.gearUnified].sort().join(',') && !p.markGearUnified(plan.done)) return;
    this.legacyDone = true;
  }

  /** 능력치를 영웅 최대치에 옮긴다. refill이면 HP·MP를 가득 채운다(레벨업) */
  private syncHeroStats(refill: boolean): void {
    const h = this.hero, n = heroSkillSpecs(this.heroId, this.jobRun.branch).length;
    h.maxHp = this.stats.maxHp; h.maxMp = this.stats.maxMp; h.mpCostCut = this.stats.mpCostCut;
    for (let i = 0; i < h.cooldownMax.length; i++) h.cooldownMax[i] = i < n ? this.stats.skillCooldown[i] : 0;
    if (refill) { h.hp = h.maxHp; h.mp = h.maxMp; }
    else { h.hp = Math.min(h.hp, h.maxHp); h.mp = Math.min(h.mp, h.maxMp); }
  }

  /** 성장 보정값을 저장된 랭크·현재 레벨에서 다시 계산하고 능력치에 반영한다(로드·배우기·초기화 때) */
  syncGrowth(): void {
    this.growthEpoch = this.growth.snapshotEpoch;
    this.jobRun?.refresh(); // 전직 스킬 비트(C10-5)를 지금 영웅 장부에 붙인 뒤 계산한다(생성자에서 jobRun보다 먼저 불릴 수 있다)
    growthMods(this.gmods, this.growth.level, this.growth.ranks, this.heroId, this.growth.kept);
    this.statAlloc.refresh(); // 다른 탭·계정 적용이 바꾼 배분(로드·배우기·영웅 교체 때만 읽는다)
    this.mastery?.refresh(); // 숙련 EXP(성장 원장 넘친 몫)도 같은 때만 다시 센다
    this.statAlloc.applyTo(this.gmods);
    this.slots.sync(this.growth);
    this.refreshStats();
  }

  /**
   * 페이지가 숨겨지거나 떠날 때(main.ts: pagehide, visibilitychange hidden) 이번 판 EXP를 지금까지 한 번 기록한다.
   * 판은 계속된다. 기록 뒤의 EXP는 새 영수증으로 쌓이므로 같은 EXP를 두 번 지급하지 않는다.
   */
  saveRunProgress(): void {
    // 모든 영웅의 판 EXP를 기록한다(영웅을 바꾼 뒤에도 이전 영웅의 남은 판 EXP·미확인 영수증이 유실되지 않게).
    // run이 없는 영웅은 commitRun이 미확인 영수증만 다시 확인하고 끝나므로 안전하다
    for (const id of PLAYABLE_HEROES) this.growthByHero[id].commitRun();
    this.gear.flush(true);
    this.heroSelect.flush();
  }

  /**
   * 다른 탭이 쓴 스테이지 진행을 읽어 합친다(main.ts: 탭이 다시 보이거나 창이 포커스를 얻을 때, 고정 스텝 밖).
   * 마을에 서 있던 탭에도 다른 탭이 새로 연 지역 문·워프 칸이 바로 보인다(문·칸은 매 프레임 progress를 읽는다).
   * 서 있는 마을·판은 바꾸지 않는다(다른 탭의 마을 이동·이동 대기는 다음 장면 전환 loadMap이 합친다)
   */
  refreshProgress(): void {
    this.progress.refresh();
    this.hard.refresh();
    this.hell.refresh();
  }

  /**
   * 미뤄 둔 저장(스킬 슬롯 배치·페이지)을 한 번에 쓴다. 고정 스텝 밖(렌더 프레임·pagehide)에서 부른다.
   * 고정 스텝 안에서는 직렬화·localStorage 쓰기를 하지 않으므로, 변경은 메모리에만 반영해 두고 여기서 합쳐 쓴다.
   */
  flushSaves(): void {
    for (const id of PLAYABLE_HEROES) this.slotsByHero[id].flush();
    this.heroSelect.flush();
    // 장비 줍기 저장은 1초에 한 번 이하로 합친다(c015). 장면 전환·페이지 숨김은 flow·saveRunProgress가 flush(true)로 바로 쓴다
    this.gear.flushIdle();
    this.field.flush();
  }

  /**
   * 장비 장부가 바뀌었으면(줍기·명령·다른 탭) 잔액을 다시 계산하고, 마을에서는 능력치도 맞춘다(던전 도중 장착 변화는 다음 판).
   * 강화 상한·보호 주문서 해금도 여기서 정한다: 지금 맵은 지역1·2뿐이라 R1 첫 클리어 = 마을② 도착, R2 첫 클리어 = 마을③ 도착(world.ts nextTown).
   */
  private owedBooted = false;
  private lastProfileMode: string = "";
  private owedTimer = 0;
  syncGear(): void {
    // 지역6 클리어(world.ENHANCE_MAX_UNLOCK_REGION, +15·보호 주문서): 지도가 지역4까지라 아직 닿지 않는다(진행 저장은 6지역까지 같은 규칙)
    const region6 = this.progress.cleared >= ENHANCE_MAX_UNLOCK_REGION * STAGES_PER_REGION;
    const town = this.highestTown();
    // 마을이 바뀔 때만 상한을 다시 구한다(enhanceCap은 배열을 거르므로 매 틱 부르지 않는다)
    if (this.gear.town !== town || this.gear.protectUnlocked !== region6 || this.gearRevision < 0) {
      this.gear.enhanceCap = enhanceCap(town, region6); this.gear.protectUnlocked = region6; this.gear.town = town; this.gear.revision++;
    }
    // 성공이라 알린 연산이 나중에 거절됐다(다른 탭 덮어쓰기 뒤 재적용): 이벤트로 알린다(c015). 문구는 gear.notice
    if (this.gear.notice.seq !== this.gearNoticeSeq) {
      // 한 프레임(flush 한 번)에 여러 연산이 거절됐으면 건수를 함께 낸다(마지막 것만 알리지 않게, PR #157 리뷰 P2)
      const count = Math.max(1, this.gear.notice.seq - this.gearNoticeSeq);
      this.gearNoticeSeq = this.gear.notice.seq;
      const k = this.gear.notice.kind;
      const e = this.emit(Ev.GearNotice);
      e.a = k === "equip" ? 1 : k === "unequip" ? 2 : k === "enhance" ? 3 : k === "salvage" ? 4 : k === "buyProtect" ? 5 : k === "recover" ? 6 : 7;
      e.b = count;
    }
    // 잔액은 확정 장비 순지출(committedGoldNet)만 읽는다: 그 값이 바뀔 때만 다시 계산한다(던전 줍기마다 잔액 계산·배열 할당을 하지 않는다, c015)
    // 미정산 제작 골드(owed)도 잔액에서 미리 뺀다: 모든 지출 경로가 같은 잔액을 보게(정산 전 다른 구매로 써 버리지 않게, PR #262 Codex P2)
    if (this.gearRevision !== this.gear.revision || this.avatarOwedRev !== this.avatar.revision) {
      this.gearRevision = this.gear.revision; this.avatarOwedRev = this.avatar.revision;
      const net = this.gear.committedGoldNet + this.owedGold();
      if (net !== this.gearGoldNet) { this.gearGoldNet = net; this.profile.recompute(); }
    }
    // 마을: 장비 장부가 능력치 사본보다 새로우면 맞춘다(던전에서 돌아온 직후 포함). 던전 안에서는 입장 때 사본 그대로
    if (this.flow.scene === Scene.Village && this.statsGearRev !== this.gear.revision) this.refreshStats();
  }

  /**
   * 도착한 가장 높은 마을: 지역 n의 보스(n-10)를 처음 깨면 마을 n+1에 도착한다(world.ts nextTown). 스테이지 진행(D3a)에서 읽는다 —
   * 이전 빌드 저장은 progress가 save.v1 기록에서 이관한다(R1 클리어 = 마을②, R2 클리어 = 마을③, 이전 규칙과 같은 값)
   */
  highestTown(): number { return this.progress.highestTown; }
  /** 레이드 주 번호(월요일 00:00 KST, 보호 주문서 주간 한도와 같은 경계) */
  raidWeek(): number { return weekIndex(this.wallClock()); }

  /**
   * 일일 의뢰 수령(L9): 마을에서만. 연산 id = 의뢰·날짜(같은 날 두 번 눌러도·두 탭이어도 장부가 한 번만 받는다) → 바로 지갑 정산.
   * 결과는 Ev.DailyClaim(a 1 성공·0 실패, b 받은 골드)
   */
  private dailyClaim(quest: string): void {
    const e = this.emit(Ev.DailyClaim);
    e.a = 0; e.b = 0;
    if (this.flow.scene !== Scene.Village || this.flow.paused) return;
    const day = this.daily.refresh().day;
    const res = this.daily.apply({ id: "claim-" + quest + "-" + day, t: "questClaim", quest }, this.highestTown(), this.gear.protectUnlocked);
    if (res.code !== "ok") return;
    settleDaily(this, true);
    e.a = 1; e.b = res.reward?.gold ?? 0;
  }

  /**
   * 주간 이벤트 목표 수령(C5): goal = 목표 키, null이면 마무리 보상. 연산 id = 목표·이벤트 주(같은 주 두 번·두 탭이어도 장부가 한 번만) → 바로 지갑 정산.
   * 결과는 Ev.DailyClaim(a 1 성공·0 실패, b 받은 골드)
   */
  private liveClaim(goal: string | null): void {
    const e = this.emit(Ev.DailyClaim);
    e.a = 0; e.b = 0;
    if (this.flow.scene !== Scene.Village || this.flow.paused) return;
    const s = this.daily.refresh();
    const week = s.weekly.week;
    const op = goal === null ? { id: "live-fin-w" + week, t: "liveFinale" as const } : { id: "live-" + goal + "-w" + week, t: "liveClaim" as const, goal };
    const res = this.daily.apply(op, this.highestTown(), this.gear.protectUnlocked);
    if (res.code !== "ok") return;
    settleDaily(this, true);
    e.a = 1; e.b = res.reward?.gold ?? 0;
  }

  /** 레이드 입장(RAID-PRD 1절): 마을(지휘소 창)·결과·패배 화면에서, 해금 마을에 도착했을 때만 */
  private enterRaid(id: string, diff?: string): void {
    const f = this.flow, deny = (why: number) => { this.emit(Ev.RaidDenied).a = why; };
    if (!isRaidId(id)) { deny(2); return; }
    // 하드(C13): 하드 판이 있고 어느 영웅이든 1차 환생을 했을 때만(4 = 하드 잠김)
    const hard = diff === "hard";
    if (hard && !hardUnlocked(id, this.maxRebirthCycle())) { deny(4); return; }
    const sceneOk = (f.scene === Scene.Village && !f.paused && f.travelTo === 0) || f.scene === Scene.Defeat
      || (f.scene === Scene.Result && !(this.result.cleared && this.result.pickedCard < 0));
    if (!sceneOk) { deny(3); return; }
    const unlock = raidDef(id).unlock;
    if ("town" in unlock && this.highestTown() < unlock.town) { deny(1); return; }
    this.flowController.loadMap("r2", raidStageKey(id, hard ? "hard" : "normal"));
  }
  /** 영웅 중 가장 높은 환생 회차(레이드 하드 해금) */
  maxRebirthCycle(): number { let c = 0; for (const h of PLAYABLE_HEROES) c = Math.max(c, this.rebirth.cycleOf(h)); return c; }
  /** 지금 판 지역(마을이면 r1): 몹 레벨·장비 방어 곡선 기준 */
  get regionKey(): RegionId { return this.map.id === "village" ? "r1" : this.flowController.plan.region; }

  /** 마을 이동 속도 배율(아바타 편의 능력치 — 마을 장면에서만, 전투 이동과 별개) */
  get townMoveMult(): number { return 1 + this.avatar.totals.townMove; }

  /** 아바타 명령(레인 E): 장착·해제·외형 숨기기는 마을에서만(장비와 같은 규칙). 바뀌면 능력치를 다시 계산한다 */
  private avatarCommand(cmd: Extract<GameCommand, { type: "avatarEquip" | "avatarUnequip" | "avatarHide" }>): void {
    if (this.flow.scene !== Scene.Village || this.flow.paused || this.flow.travelTo > 0) return;
    const code = cmd.type === "avatarEquip" ? this.avatar.equip(cmd.id) : cmd.type === "avatarUnequip" ? this.avatar.unequip(cmd.slot as AvatarSlot) : this.avatar.setHide(cmd.hide);
    if (code === "ok") this.refreshStats();
  }

  /**
   * 아바타 제작(E3, PRD 1.6): 최신 잔액으로 사전 검사 → 아바타 쓰기 한 번(조각 차감·지급·연산 id·미정산 골드 owed) → 곧바로 골드 정산.
   * 환불 경로는 없다: 쓰기가 실패하면 아무것도 바뀌지 않고, 정산이 끊겨도 owed가 남아 다음 정산(재시도·게임 시작·주기)이 골드를 한 번만 받는다.
   * 결과 띠는 아바타 쓰기가 성공이면 ok(결제가 남았어도 owed가 처리한다)
   */
  private avatarCraft(cmd: Extract<GameCommand, { type: "avatarCraft" }>): void {
    const note = (code: AvatarCraftCode): void => this.avatar.noteCraft(cmd.id, code);
    if (this.flow.scene !== Scene.Village || this.flow.paused || this.flow.travelTo > 0) { note("locked"); return; }
    const r = craftRecipe(cmd.id);
    if (!r) { note("unknown"); return; }
    if (this.profile.mode !== "ok") { note("readonly"); return; }
    this.avatar.read(); // 다른 탭의 조각·보유·owed 변경을 먼저 반영
    this.syncGear(); // 같은 step에 앞선 명령이 쓴 골드와 owed 예약을 잔액에 반영(큐된 제작이 낡은 잔액으로 검증하지 않게)
    const pre = craftCheck(r, this.avatar.shards, this.profile.gold, this.avatar.owned);
    if (pre !== "ok") { note(pre); return; }
    const code = this.avatar.craft(cmd.id, "avc:" + cmd.requestId, r.gold);
    this.settleAvatarOwed();
    if (code === "ok" || code === "dup") { note("ok"); this.refreshStats(); return; }
    note(code === "owned" ? "owned" : code === "no_shards" ? "no_shards" : "write_failed");
  }

  /**
   * 아바타 제작 미정산 골드 정산: owed 항목마다 장비 장부에 골드 차감(연산 id mk:<op> 고정이라 몇 번 불러도 한 번만).
   * ok·duplicate면 owed에서 지운다. no_gold·저장 실패면 남겨 두고 다음에 다시 한다. 매번 잔액을 다시 동기화한다
   */
  /** 미정산 제작 골드 합(잔액 예약분) */
  private owedGold(): number {
    const o = this.avatar?.owed;
    let sum = 0;
    // 장부에 이미 들어간 결제(clearOwed 쓰기만 실패한 경우)는 committedGoldNet에 반영돼 있으니 두 번 빼지 않는다(PR #262 Codex P2)
    if (o) for (let i = 0; i < o.length; i++) if (!this.gear?.knowsOp("mk:" + o[i].op)) sum += o[i].gold;
    return sum;
  }

  private settleAvatarOwed(): void {
    if (this.profile.mode !== "ok" || this.avatar.owed.length === 0) return;
    for (const o of [...this.avatar.owed]) {
      // 다른 탭이 방금 쓴 골드까지 읽고 넘긴다(mkGold 검사는 넘긴 잔액만 보므로 낡은 값이면 실제 잔액을 넘겨 차감될 수 있다)
      this.gear.refresh(); this.syncGear();
      // 잔액(profile.gold)은 이 항목을 이미 예약해 뺀 값이라 정산은 그 예약분을 더해 넘긴다
      const paid = this.gear.applyMarket({ id: "mk:" + o.op, t: "mkGold", d: -o.gold, gold: this.profile.gold + o.gold });
      this.syncGear();
      if (paid === "ok" || paid === "duplicate") this.avatar.clearOwed(o.op);
    }
  }

  /**
   * 도감 단계 보상 받기(E3, PRD 2.2): 도감 수 ≥ 단계이고 아직 안 받았을 때. 지급은 단계별 고정 id(보석 연산 id·아바타 opId)라
   * 지급 뒤 "받음" 표시 전에 끊겨도 다시 받기가 한 번만 지급한다. 전투 능력치 보상 없음(보석은 소켓 재료, 칭호·오라는 외형)
   */
  private adventureClaim(cmd: Extract<GameCommand, { type: "adventureClaim" }>): void {
    if (this.flow.scene !== Scene.Village || this.flow.paused || this.flow.travelTo > 0) return;
    const rw = codexRewardOf(cmd.step);
    if (!rw || this.profile.mode !== "ok") return;
    this.adventure.read(); this.avatar.read();
    if (this.adventure.claimed.includes(rw.step)) return;
    if (codexCount(codexOf(this.adventure.found, this.avatar.owned)) < rw.step) return;
    if (rw.kind === "gems") {
      // 색마다 깨진(1등급) 보석. 'cd:' 접두사 연산 id는 장부가 오래 기억한다
      for (let color = 0; color < GEM_COLORS.length; color++) {
        const res = this.gear.submit({ id: `cd:codex${rw.step}:${color}`, t: "gems", code: gemCode(color, 1), n: CODEX_GEM_EACH });
        if (res.code !== "ok" && res.code !== "duplicate") return;
      }
      if (!this.gear.flush(true)) return; // 저장 확인 전에는 받음 표시를 하지 않는다(다시 누르면 같은 id라 한 번만)
    } else if (rw.kind === "aura") {
      const code = this.avatar.award(CODEX_AURA_AVATAR, "cx:aura");
      if (code !== "ok" && code !== "dup" && code !== "owned") return;
    }
    if (this.adventure.claim(rw.step)) this.refreshStats();
  }

  /** 보스 처치 아바타 드롭(전용 난수, 바닥에 놓지 않고 바로 장부에 넣는다 — 획득 띠는 UI가 lastGain으로 띄운다) */
  avatarBossDrop(): void {
    const t = this.loot.table, tier = t === "drop_final" ? "final" : t === "drop_mid" ? "mid" : null;
    const st = tier ? stageById(this.loot.stageId) : undefined;
    if (!tier || !st) return;
    // 판마다 다시 시드한다(판 고유값 + 스테이지): 접속마다 같은 순서로 굴러 "N번째 처치에 확정"을 외우거나 새로고침으로 노리지 못하게(PR #242 리뷰 P1)
    const run = this.loot.runKey;
    const rng = seedRng(hashSeed(`${this.avatarSeed}:${run}:${st.id}:avatar`));
    const id = rollAvatarDrop(() => nextRand(rng), st.region, tier);
    // 연산 id는 판·스테이지로 고정(같은 처치가 두 번 들어와도 한 번만)
    if (id) this.avatar.grant(`av:${run}:${st.id}`, id);
  }

  /**
   * 현상금(레인 E 2단계, AVATAR-ADVENTURE-PRD 2.2): 오늘 대상 보스를 잡으면 완료 표시 + 그 보스의 득템 판정 1회 추가
   * (이름 있는 유니크는 바닥에, 아바타는 장부에). 같은 날 같은 대상은 한 번만. 골드·강화석은 주지 않는다(경제 불변)
   */
  adventureBossKill(x: number, z: number): void {
    const st = stageById(this.loot.stageId);
    const t = this.loot.table, tier = t === "drop_final" ? "final" : t === "drop_mid" ? "mid" : null;
    if (!st || !tier) return;
    const now = this.wallClock();
    const b = bountyFor(this.adventure.dayAt(now), this.highestTown(), st.id);
    if (!b || !this.adventure.complete(b.id, now)) return;
    const run = this.loot.runKey;
    const rng = seedRng(hashSeed(`${this.avatarSeed}:${run}:${st.id}:bounty`));
    const rand = (): number => nextRand(rng);
    for (const u of namedFor(st.region, tier)) if (rand() < u.source.chance) this.loot.spawnNamed(x, z, u.id);
    const id = rollAvatarDrop(rand, st.region, tier);
    if (id) this.avatar.grant(`avb:${run}:${st.id}`, id);
  }

  /** 장비 명령(마을): 문맥 거절 → 연산 만들기 → 장부 쓰기 → 결과 이벤트 */
  private gearCommand(cmd: Extract<GameCommand, { type: "gearEquip" | "gearUnequip" | "gearEnhance" | "gearSalvage" | "gearBuyProtect" }>): void {
    const kind: Exclude<GearCmdKind, "none" | "pickup" | "recover"> = cmd.type === "gearEquip" ? "equip" : cmd.type === "gearUnequip" ? "unequip" : cmd.type === "gearEnhance" ? "enhance" : cmd.type === "gearSalvage" ? "salvage" : "buyProtect";
    const denied = this.village.gearDenial(kind);
    const economy = kind === "enhance" || kind === "salvage" || kind === "buyProtect";
    const uid = "uid" in cmd ? cmd.uid : "";
    const code = this.gear.command(kind, cmd.requestId, (id): GearOp | GearCode => {
      if (denied) return denied;
      // 골드가 움직이는 연산은 거래 권한(이 탭이 상점 장부 writer)이 있을 때만(다른 탭과 잔액이 엇갈리지 않게)
      if (economy && this.profile.mode !== "ok") return "readonly";
      const gold = this.profile.gold;
      switch (cmd.type) {
        // c022: 무기 칸은 계정 공유 한 칸(gear.v1 형식 그대로)이라 카엘 외 영웅은 무기를 끼거나 벗을 수 없다(카엘 무기 칸 보호). 영웅별 무기는 남은 게이트
        // 무기 칸은 영웅별(lyra-weapon-slot): 장착·벗기 모두 지금 영웅의 칸. 다른 영웅 전용 무기는 장부가 wrong_hero로 거절
        case "gearEquip": return { id, t: "equip", uid: cmd.uid, hero: this.heroId };
        case "gearUnequip": return cmd.slot === "weapon" && this.heroId !== "kael" ? { id, t: "unequip", slot: cmd.slot, hero: this.heroId } : { id, t: "unequip", slot: cmd.slot };
        case "gearSalvage": return { id, t: "salvage", uid: cmd.uid };
        case "gearEnhance": return { id, t: "enhance", uid: cmd.uid, protect: cmd.protect, cap: this.gear.enhanceCap, region6: this.gear.protectUnlocked, gold };
        case "gearBuyProtect": return { id, t: "buyProtect", week: weekIndex(this.wallClock()), town: this.highestTown(), region6: this.gear.protectUnlocked, gold };
      }
    }, uid);
    if (code === "duplicate") return;
    const e = this.emit(Ev.GearCmd);
    e.a = code === "ok" ? 1 : 0;
    e.b = kind === "equip" ? 1 : kind === "unequip" ? 2 : kind === "enhance" ? 3 : kind === "salvage" ? 4 : 5;
    if (kind === "enhance" && code === "ok") {
      const l = this.gear.last, en = this.emit(Ev.GearEnhance);
      en.a = l.outcome === "success" ? 0 : l.outcome === "keep" ? 1 : l.outcome === "down" ? 2 : 3;
      en.b = l.to; en.c = l.from; en.d = l.chance;
    }
    this.syncGear();
  }

  /**
   * 소비 아이템·보석 명령(D2c): 문맥 거절 → 연산 → 장부 쓰기 → GearCmd 이벤트(b 7 구매 8 제작 9 비약 10 박기 11 빼기 12 합성).
   * 구매·제작은 잡화상 창, 보석은 대장간 창(마을③부터), 비약은 마을·던전 어디서나(일시정지·결과 화면 제외)
   */
  private consCommand(cmd: Extract<GameCommand, { type: "consBuy" | "consCraft" | "useElixir" | "gemSocket" | "gemUnsocket" | "gemCombine" }>): void {
    const kind = cmd.type === "consBuy" ? "buy" : cmd.type === "consCraft" ? "craft" : cmd.type === "useElixir" ? "elixir" : cmd.type === "gemSocket" ? "socket" : cmd.type === "gemUnsocket" ? "unsocket" : "combine";
    const town = this.highestTown();
    const f = this.flow;
    let denied: GearCode | undefined;
    if (kind === "elixir") denied = f.paused || (f.scene !== Scene.Village && !combatScene(f.scene)) || this.hero.hp <= 0 ? "denied" : undefined;
    else denied = this.village.gearDenial(kind);
    if (!denied && (kind === "socket" || kind === "unsocket" || kind === "combine") && town < JEWELER_TOWN) denied = "locked";
    if (!denied && kind === "craft" && town < ALCHEMIST_TOWN) denied = "locked";
    // 비약 "한 번에 사용": 가진 계열을 모두 한 병씩(각각 연산, 같은 요청에서 나온 id라 재전송도 멱등)
    if (cmd.type === "useElixir" && cmd.item === "all") {
      let any = false, last: GearCode = "no_item";
      for (const el of ELIXIRS) {
        if ((this.gear.cons[el.id] ?? 0) < 1) continue;
        last = this.useElixirOne(el.id, cmd.requestId + ":" + el.fam, denied);
        if (last === "ok") any = true;
      }
      if (last === "duplicate") return;
      this.gearCmdEvent(any ? "ok" : last, 9);
      return;
    }
    if (cmd.type === "useElixir") {
      const code = this.useElixirOne(cmd.item, cmd.requestId, denied);
      if (code !== "duplicate") this.gearCmdEvent(code, 9);
      return;
    }
    const economy = this.profile.mode !== "ok";
    const code = this.gear.command(kind, cmd.requestId, (id): GearOp | GearCode => {
      if (denied) return denied;
      if (economy) return "readonly";
      const gold = this.profile.gold;
      switch (cmd.type) {
        case "consBuy": return { id, t: "buy", item: cmd.item, n: cmd.n, town, gold };
        case "consCraft": return { id, t: "craft", item: cmd.item, n: cmd.n, town, gold };
        case "gemSocket": return { id, t: "socket", uid: cmd.uid, idx: cmd.idx, code: cmd.code, town };
        case "gemUnsocket": return { id, t: "unsocket", uid: cmd.uid, idx: cmd.idx, town, gold };
        case "gemCombine": return cmd.code >= 0 && cmd.code < GEM_CODES ? { id, t: "combine", code: cmd.code, town, gold } : "denied";
      }
    }, "uid" in cmd ? cmd.uid : "");
    if (code === "duplicate") return;
    this.gearCmdEvent(code, kind === "buy" ? 7 : kind === "craft" ? 8 : kind === "socket" ? 10 : kind === "unsocket" ? 11 : 12);
  }

  /**
   * S7 마을 장비 상점 구매(STAGE-WORLD-PRD 5.2): 지금 머문 마을의 품목만, 그 품목을 파는 NPC 창(잡화상 = 방어구·장신구, 대장간 = 무기)이 열린 동안.
   * 이전 마을 품목은 워프로 그 마을에 가서 산다. GearCmd 이벤트 b 13
   */
  private gearBuy(cmd: Extract<GameCommand, { type: "gearBuy" }>): void {
    const o = townGearOffer(cmd.offer);
    let denied: GearCode | undefined = this.village.gearDenial("buyGear");
    if (!denied && (!o || o.town !== this.flow.town)) denied = "locked";
    if (!denied && o && npcDef(this.village.menuNpc)?.role !== o.seller) denied = "denied";
    if (!denied && o && !townGearBuyableBy(o, this.heroId)) denied = "wrong_hero";
    const economy = this.profile.mode !== "ok";
    const town = this.flow.town, hero = this.heroId;
    const code = this.gear.command("buyGear", cmd.requestId, (id): GearOp | GearCode => {
      if (denied) return denied;
      if (economy) return "readonly";
      return { id, t: "buyGear", offer: cmd.offer, town, hero, gold: this.profile.gold };
    });
    if (code === "duplicate") return;
    this.gearCmdEvent(code, 13);
  }

  /** 비약 한 병: 장부 명령(마신 순간 플레이 시계), 성공하면 능력치·드롭률에 바로 반영(Ev.Consume a 2) */
  private useElixirOne(item: string, requestId: string, denied: GearCode | undefined): GearCode {
    const k = ELIXIRS.findIndex((e) => e.id === item);
    const at = this.gear.play;
    const code = this.gear.command("elixir", requestId, (id): GearOp | GearCode => denied ?? (k < 0 ? "denied" : { id, t: "elixir", item, at }));
    if (code !== "ok") return code;
    this.refreshBuffs();
    const e = this.emit(Ev.Consume);
    e.x = this.hero.x; e.z = this.hero.z; e.a = 2; e.b = k; e.c = Math.max(0, this.gear.elix[k] - this.gear.play);
    return code;
  }

  private gearCmdEvent(code: GearCode, b: number): void {
    const e = this.emit(Ev.GearCmd);
    e.a = code === "ok" ? 1 : 0; e.b = b;
    this.syncGear();
  }

  /**
   * 귀환서(D2c): 던전에서 한 장 써서 바닥에 남은 드롭을 모두 거두고 마을로 간다(일시정지 '마을로'는 바닥 드롭을 버린다).
   * 장부 명령으로 쓰고(재전송 멱등), 실패하면 마을로 가지 않는다. 결과는 GearCmd b 13
   */
  private returnScroll(requestId: string): void {
    const f = this.flow;
    // 파티 판에서는 귀환서를 쓰지 않는다(판 연결·서버 몹 동기화가 마을까지 따라오지 않게 — PR #243 Codex P2). 파티 판은 파티 창 「나가기」로 떠난다
    const denied: GearCode | undefined = !combatScene(f.scene) || this.hero.hp <= 0 || this.party.view.active || this.raidNet.active ? "denied" : undefined;
    const code = this.gear.command("use", requestId, (id): GearOp | GearCode => denied ?? { id, t: "use", item: "scroll_return" });
    if (code === "duplicate") return;
    if (code === "ok") {
      const e = this.emit(Ev.Consume);
      e.x = this.hero.x; e.z = this.hero.z; e.a = 3;
      this.loot.sweep(true); // 클리어 자동 줍기 설정과 무관하게 항상 거둔다
      if (f.paused) f.paused = false;
      this.flowController.loadMap("village");
    }
    this.gearCmdEvent(code, 13);
  }

  /**
   * EXP 획득(처치·보스·클리어). 던전 안에서 즉시 오르며, 레벨이 오르면 LevelUp을 내고 능력치를 다시 계산해 HP·MP를 채운다.
   * cause는 XpCause(0 처치, 1 보스, 2 클리어). x,z는 연출 위치다.
   */
  grantXp(rawAmount: number, cause: number, x: number, z: number): void {
    // 용사의 시련 판은 경험치가 없다(도전 모드 — 시련 재도전이 사냥을 대신해 레벨 곡선을 앞당기지 않게, content/trials.ts 머리말)
    if (this.trialRun.running) return;
    // 레벨 차 보정(SKILL-TREE 1절): 지역 몹 레벨보다 5↑ ×0.5, 10↑ ×0.1. 처치·보스·클리어 모두
    const mobLevel = this.mobLevel;
    // 장비 EXP 획득 옵션(BUFF_CAP 50%까지, 던전 입장 때 고정)을 보정 전 값에 곱한다(잠금 중 재보정도 같은 값을 쓴다)
    // 환생 회차 EXP +50%(레인 R, 1차 환생부터 — 장비 옵션 캡과 별개)
    // 다른 영웅 계정 보너스(C12): 다른 영웅이 Lv99(또는 환생)에 닿은 캐릭터의 1회차 영웅은 EXP ×2.86(1→99 = 첫 영웅 시간의 35%)
    rawAmount = Math.round(rawAmount * this.stats.xpMult * this.rebirth.xpMult * this.altXpMult);
    const amount = adjustedXp(rawAmount, this.growth.level, mobLevel);
    if (!(amount > 0)) return;
    const before = this.growth.level;
    // 저장 잠금 중에는 표시 레벨이 자리값이라 raw·몹 레벨도 넘겨 복구 때 실제 레벨로 다시 보정한다
    this.growth.addRunXp(amount, rawAmount, mobLevel);
    const e = this.emit(Ev.XpGain); e.x = x; e.z = z; e.a = amount; e.b = cause;
    const after = this.growth.level;
    if (after > before) {
      growthMods(this.gmods, after, this.growth.ranks, this.heroId, this.growth.kept);
      this.statAlloc.applyTo(this.gmods);
      // 레벨 동기화(레인 R): 레벨이 오르면 장비 수치가 그 레벨 기준으로 돌아온다(입장 때 고정한 비약은 다시 더한다)
      if (this.rebirth.cur.cycle >= 1) { this.rebirth.syncGear(this.statsGear); applyElixirs(this.statsGear, this.statsBuffMask); applyAvatarTotals(this.statsGear, this.avatar.totals); }
      this.derive(this.statsFocus);
      this.syncHeroStats(true);
      const lu = this.emit(Ev.LevelUp);
      lu.x = this.hero.x; lu.z = this.hero.z; lu.a = after; lu.b = Math.max(0, this.growth.points - this.growth.spent); lu.c = after - before;
      this.growth.lastLevelUpTick = lu.tick; this.growth.lastLevelUpLevel = after;
      this.slots.sync(this.growth); // 새 스킬은 빈 슬롯에 자동 배치(D1e)
      // 새 스킬 자동 습득 알림(D1e): 이번 레벨업으로 열린 스킬마다 한 건. growthMods가 이미 랭크 1을 줬으므로 다음 입력부터 쓴다
      const hid = this.heroId, opened = skillsLearnedBetween(before, after, hid);
      if (opened !== 0) {
        const rows = HERO_SKILL_TABLE[hid].autoLearn;
        for (let i = 0; i < rows.length; i++) {
          const id = rows[i].id;
          if ((opened & skillBit(id)) === 0) continue;
          const sl = this.emit(Ev.SkillLearned);
          sl.x = this.hero.x; sl.z = this.hero.z; sl.a = id; sl.b = after; sl.c = nextSkillLevel(after, hid);
        }
      }
    }
  }

  /** 한 판 단위 초기화(loadMap/retry): 텔레그래프·콤보·각성·히트스톱·세계 시계·입력 버퍼 */
  resetRun(): void {
    this.village.reset();
    this.hitstop = 0;
    this.koSlow = 0; this.koTick = -1;
    this.justDodgeSlow = 0; this.dodgeAge = -1; this.dodgeHit = false; this.justDodgeCounter = 0;
    this.massSlow = 0;
    this.worldAcc = 0; this.worldFrac = 0; this.worldTick = false;
    this.bufferedButtons = 0;
    this.releaseAllTelegraphs();
    this.threats.reset(); this.hazards.reset(); this.hellBrand.reset();
    const c = this.combo; c.count = 0; c.timer = 0; c.best = 0; c.praiseTier = 0;
    const l = this.link; l.stage = 0; l.timer = 0; l.used = 0; l.cancelReady = 0; l.finishes = 0; l.best = 0;
    this.combat.reset();
    this.magnet.target = -1; this.magnet.boss = false;
    this.rampageController.reset();
    this.awakening.reset();
    this.skillFx.clear();
    this.arrows.clear();
    // 지역 상태 이상(D3a)은 판을 넘기지 않는다(결과 화면에서 바로 재도전해도 둔화·중독이 남지 않게)
    this.hero.chillTicks = 0; this.hero.poisonTicks = 0;
    // 스페셜 몬스터 판 계획(스테이지 판의 일반 스테이지만): loadMap이 판 계획을 정한 뒤 부른다
    this.special.begin();
  }

  /** 이벤트 발행. 명령 처리 중(틱 증가 전)에 생긴 이벤트는 이번 step의 틱으로 기록한다. */
  emit(type: number): GameEvent {
    return this.events.push(type, this.applyingCommand ? this.tick + 1 : this.tick);
  }

  /**
   * 결투장식 KO(엘리트·방의 마지막 몹·보스를 쓰러뜨린 순간): 히트스톱을 KO.hitstop으로 올리고, 정지가 풀리면 세계를 잠깐 늦춘다.
   * 렌더는 Ev.Ko로 흰 별·붉은 X·화면 어둡게·푸시인을 건다. 각성 중에는 각성 연출이 우선이라 걸지 않는다.
   */
  /** 대량 처치 슬로(COMBAT-FEEL 2.14): 세계만 잠깐 늦춘다. 각성 중에는 각성 시간표가 우선이라 걸지 않는다(난무로 20마리를 쓸어도 각성 길이가 바뀌지 않게) */
  startMassSlow(): void {
    if (this.awaken.phase !== AwakenPhase.None) return;
    this.massSlow = Math.max(this.massSlow, MASS_SLOW.slowTicks);
  }

  startKo(kind: "elite" | "room" | "boss", x: number, z: number): void {
    if (this.awaken.phase !== AwakenPhase.None || this.koTick === this.tick) return;
    this.koTick = this.tick;
    // 스테이지를 끝내는 마지막 몹도 보스 결정타처럼 길게 보여 주고, 클리어 연출 대기(CLEAR_HOLD)를 건다
    const big = kind === "boss" || this.flowController.endsStage();
    if (big) this.flowController.beginClearHold(x, z, kind === "boss");
    this.koSlow = big ? KO.bossSlowTicks : KO.slowTicks;
    this.combat.stop(big ? KO.bossHitstop : KO.hitstop);
    const e = this.emit(Ev.Ko);
    e.x = x; e.z = z; e.a = kind === "elite" ? 0 : kind === "room" ? 1 : 2;
  }

  private apply(cmd: GameCommand): void {
    switch (cmd.type) {
      case "openInventory": this.village.openInventory(); break;
      case "openStageMap": this.village.openStageMap(); break;
      case "openQuest": this.village.openQuest(); break;
      case "closeMenu": this.village.closeMenu(); break;
      case "buy": case "equip": case "setSkillFocus": case "recoverProfile": {
        const code = this.profile.command(cmd, this.village.denial(cmd));
        const e = this.emit(Ev.Trade); e.a = code === 'ok' ? 1 : 0;
        e.b = cmd.type === 'buy' ? 1 : cmd.type === 'equip' ? 2 : cmd.type === 'setSkillFocus' ? 3 : 4;
        break;
      }
      case "start": if (this.flow.scene === Scene.Title) { this.flowController.loadMap("village"); this.dailyRun.recover(); } break;
      case "enterRaid": this.enterRaid(cmd.raid, cmd.diff); break;
      case "dailyClaim": this.dailyClaim(cmd.quest); break;
      case "liveClaim": this.liveClaim(cmd.goal); break;
      case "liveFinale": this.liveClaim(null); break;
      case "enterDaily": this.dailyRun.enter(cmd.kind); break;
      case "enterTrial": this.trialRun.enter(cmd.trial); break;
      case "jobChoose": this.jobRun.choose(cmd.branch); break;
      case "jobAdvance": this.jobRun.advance(); break;
      case "claimTrialChest": this.trialRun.claimChest(cmd.board); break;
      case "raidNetLeave": this.raidNet.leaveByRequest(); break;
      case "returnToVillage": this.flowController.loadMap("village"); break;
      case "enterDungeon":
        // stage를 주면 스테이지 입장(enterStage와 같다), region만 주면 자유 입장(지도 그대로·진행 기록 없음)
        if (cmd.stage !== undefined) { this.stageCommand(cmd.stage); break; }
        if (!this.allowFreeEntry) { const sel = this.doorStage(cmd.region ?? "r1"); if (sel) this.stageCommand(sel); else this.emit(Ev.StageLocked).a = Number((cmd.region ?? "r1").slice(1)); break; }
        if (this.flow.scene === Scene.Village || this.flow.scene === Scene.Title) this.flowController.loadMap(cmd.region ?? "r1");
        break;
      case "enterStage": this.stageCommand(cmd.stage); break;
      case "selectStage": {
        const ok = this.flow.scene === Scene.Village && !this.flow.paused && (cmd.hell ? this.hell.select(cmd.region, cmd.index) : cmd.hard ? this.hard.select(cmd.region, cmd.index) : this.progress.select(cmd.region, cmd.index));
        const e = this.emit(Ev.StageResult); e.a = -1; e.b = cmd.index; e.c = ok ? 1 : 0; e.d = 0;
        break;
      }
      case "setDifficulty": {
        // 마을 문 난이도(D3b·C10): 마을에서만. 결과는 StageResult(a −2, b 2 헬·1 하드·0 노말, c 성공).
        // 헬을 고르면 하드 선택은 그대로 두고 헬 선택을 켠다(문은 헬 → 하드 → 노말 순서로 본다). 하드·노말을 고르면 헬 선택을 끈다
        const inVillage = this.flow.scene === Scene.Village && !this.flow.paused;
        let ok = false;
        if (inVillage) ok = cmd.hell ? this.hell.setOn(true) : this.hell.setOn(false) && this.hard.setOn(cmd.hard);
        const e = this.emit(Ev.StageResult); e.a = -2; e.b = cmd.hell ? 2 : cmd.hard ? 1 : 0; e.c = ok ? 1 : 0; e.d = 0;
        break;
      }
      case "travel": this.flowController.travel(cmd.town); break;
      // 재도전: 방금 한 판과 같은 스테이지(자유 입장이면 같은 지도)
      case "retry": if (this.field.retry()) break; if (this.trialRun.showingResult) { this.trialRun.enter(this.trialRun.lastId); break; } if (this.flowController.plan.daily && this.flow.mapId !== "village") { if (this.flow.scene === Scene.Result || this.flow.scene === Scene.Defeat) this.flowController.loadMap("village"); break; } if (this.flow.scene === Scene.Result || this.flow.scene === Scene.Defeat) this.flowController.loadMap(this.flow.mapId, (this.flow.stageId || null) as AnyStageId | null); break;
      case "setPaused": if (!cmd.paused || this.pausable()) this.flow.paused = cmd.paused; break;
      case "setSetting": this.settings[cmd.key] = cmd.value; break;
      case "pickCard": this.flowController.pickCard(cmd.index); break;
      case "skipClear": this.flowController.skipClearHold(); break;
      case "learnSkill": case "resetSkills": this.skillCommand(cmd); break;
      case "selectHero": this.selectHero(cmd.hero); break;
      case "setSkillPage": if (cmd.page === undefined) this.slots.togglePage(); else this.slots.setPage(cmd.page); break;
      case "setSkillSlot": this.slots.command(cmd.slot, cmd.skill, this.growth, this.flow.scene === Scene.Village ? undefined : "not_village"); break;
      case "gearEquip": case "gearUnequip": case "gearEnhance": case "gearSalvage": case "gearBuyProtect": this.gearCommand(cmd); break;
      case "avatarEquip": case "avatarUnequip": case "avatarHide": this.avatarCommand(cmd); break;
      case "avatarCraft": this.avatarCraft(cmd); break;
      case "adventureClaim": this.adventureClaim(cmd); break;
      case "consBuy": case "consCraft": case "useElixir": case "gemSocket": case "gemUnsocket": case "gemCombine": this.consCommand(cmd); break;
      case "gearBuy": this.gearBuy(cmd); break;
      case "useReturnScroll": this.returnScroll(cmd.requestId); break;
      case "setAutoPotion": this.consumables.setAutoPotion(cmd.pct); break;
      case "gearRecover": {
        // 손상 장비 저장 새로 시작: 마을에서만(UI 두 단계 확인). 결과 문구는 gear.last
        const code = this.flow.scene === Scene.Village && !this.flow.paused ? this.gear.recover(cmd.requestId) : "denied";
        if (code === "duplicate") break;
        if (code === "denied") { const l = this.gear.last; l.seq++; l.kind = "recover"; l.code = "denied"; l.requestId = cmd.requestId; this.gear.revision++; }
        const e = this.emit(Ev.GearCmd); e.a = code === "ok" ? 1 : 0; e.b = 6;
        this.syncGear();
        break;
      }
      case "rebirth": case "rebirthAlloc": case "rebirthIntro": this.rebirth.command(cmd); break;
      case "statAlloc": this.statAlloc.command(cmd); break;
      case "masteryAlloc": case "transcend": case "masteryClaim": this.mastery.command(cmd); break;
      case "field": this.field.command(cmd.op, cmd.tier ?? 0); break;
      case "qa": this.applyQa(cmd.op, cmd.arg); break;
    }
  }

  /**
   * 스킬 트리 배우기·초기화. 마을에서 장비 창이나 교관 창이 열려 있을 때만 된다(village.skillDenial).
   * 성공하면 능력치를 다시 계산하고, 결과는 SkillLearn(a 1 성공 0 실패, b 노드, c 새 랭크)으로 낸다.
   */
  private skillCommand(cmd: GrowthCommand): void {
    // 스킬 트리(SKILL_NODES)는 카엘 전용이다(c022): 다른 영웅은 배우기·초기화를 거절한다(장부 랭크를 바꾸지 않는다)
    const code = this.growth.command(cmd, this.heroId !== "kael" ? "readonly" : this.village.skillDenial());
    // 중복 요청은 결과 이벤트도 내지 않고 조용히 무시한다(재전송·연타)
    if (code === "duplicate") return;
    const node = cmd.type === "learnSkill" ? cmd.node : -1;
    const e = this.emit(Ev.SkillLearn);
    e.a = code === "ok" ? 1 : 0; e.b = node;
    e.c = node >= 0 && node < this.growth.ranks.length ? this.growth.ranks[node] : 0;
    if (code === "ok") this.syncGrowth();
  }

  /**
   * 환생 성공 뒤(레인 R): 그 영웅의 성장 장부를 새 회차 키 장부로 바꾸고 슬롯 추가 칸·전투 보정·능력치를 맞춘다(HP·MP 가득).
   * 1회차 장부(growth.v2 / growth.lyra.v1)는 저장소에 그대로 남는다
   */
  swapGrowth(hero: PlayableHeroId, next: GrowthStore): void {
    this.growthByHero[hero] = next;
    this.slotsByHero[hero].setExtra(rebirthSlotBonus(this.rebirth.cycleOf(hero)));
    if (hero !== this.heroId) return;
    this.syncGrowth();
    this.syncHeroStats(true);
  }

  /**
   * 영웅 선택(c022): 마을에서만(던전·결과·패배·일시정지·이동 연출 중 거절). 바꾸면 그 영웅의 성장 장부·슬롯·능력치로 다시 맞추고
   * 쿨다운·각성 게이지·화살을 초기화한다. 결과는 Ev.HeroSelect. 저장은 flushSaves(고정 스텝 밖)
   */
  private selectHero(hero: PlayableHeroId): void {
    const e = this.emit(Ev.HeroSelect);
    const index = PLAYABLE_HEROES.indexOf(hero);
    e.b = index;
    if (index < 0) { e.c = 2; return; }
    if (this.flow.scene !== Scene.Village || this.flow.paused || this.flow.travelTo > 0) { e.c = 1; return; }
    if (!this.heroSelect.set(hero)) { e.c = 3; e.a = 1; return; }
    this.growth.refresh();
    this.syncGrowth();
    this.heroController.reset();
    this.arrows.clear();
    this.syncHeroStats(true);
    e.a = 1;
  }

  /**
   * 마을 문이 그 지역에서 들어갈 스테이지(D3b): 문 난이도가 하드이고 그 지역 하드가 열렸으면 고른 하드 스테이지, 아니면 노말 고른 스테이지.
   * 지역이 잠겼으면 null
   */
  doorStage(region: RegionId): string | null {
    if (this.hell.on) { const x = this.hell.selected(region); if (x) return x; }
    if (this.hard.on) { const h = this.hard.selected(region); if (h) return h; }
    return this.progress.selected(region);
  }

  /** 스테이지 입장 명령: 잠김·장면 거절은 StageLocked 이벤트로 알린다 */
  private stageCommand(stage: string): void {
    if (this.flowController.enterStage(stage)) return;
    const p = /^[rhx](\d)-/.exec(stage);
    this.emit(Ev.StageLocked).a = p ? Number(p[1]) : 0;
  }

  private applyQa(op: QaOp, arg: number): void {
    if (this.flow.scene !== Scene.Dungeon) return;
    let done = true;
    if (op === "fillAwaken") this.hero.awakenGauge = 1;
    else if (op === "gotoRoom") done = this.flowController.qaGotoRoom(arg);
    else if (op === "spawnWave") done = this.flowController.qaSpawnWave();
    else if (op === "killAll") this.flowController.qaKillAll();
    else done = false;
    if (done) this.qaOps++;
  }

  walkable(x: number, z: number, radius: number, ignoreGate = -1, mob = false): boolean {
    for (let i = 0; i < 4; i++) {
      if (!this.walkablePoint(x + (i & 1 ? radius : -radius), z + (i & 2 ? radius : -radius), ignoreGate, mob)) return false;
    }
    return true;
  }

  private walkablePoint(x: number, z: number, ignoreGate = -1, mob = false): boolean {
    if ((mob && lavaAt(this.grid, x, z)) || cellAt(this.grid, x, z) !== Cell.Floor) return false;
    const gate = gateAt(this.grid, x, z);
    return gate === ignoreGate || gate < 0 || this.flow.gateOpen[gate] >= 0.99;
  }

  /** 큰 원형 몸체(보스) 충돌: 중심·안쪽 고리·바깥 고리 표본으로 기둥을 사이에 끼고 지나가지 않게 한다. */
  walkableBoss(x: number, z: number, radius: number): boolean {
    if (!this.walkablePoint(x, z, -1, true)) return false;
    for (let ring = 0; ring < 2; ring++) {
      const r = radius * (ring === 0 ? 0.5 : 0.95), n = ring === 0 ? 8 : 20;
      for (let i = 0; i < n; i++) {
        const a = (i * Math.PI * 2) / n;
        if (!this.walkablePoint(x + Math.sin(a) * r, z + Math.cos(a) * r, -1, true)) return false;
      }
    }
    return true;
  }

  /** 일반 몹 HP·피해 배율: 지금 판 계획(지역 배율 × 스테이지 배율, 자유 입장은 지역 배율만) */
  get mobHpMult(): number { return this.map.id === "village" ? 1 : this.flowController.plan.mobHpMult; }
  get mobDamageMult(): number { return this.map.id === "village" ? 1 : this.flowController.plan.mobDamageMult; }
  /** 일반 몹 공세(지역별 동시 공격 수·선딜 결심·재사용 대기, c018). 마을은 기본 값 */
  get mobTempo(): MobTempo { return this.map.id === "village" ? BASE_TEMPO : REGIONS[this.flowController.plan.region].mobTempo; }

  moveHero(dx: number, dz: number): void {
    // 빠른 돌진도 한 셀 벽을 통과하지 않게 잘게 나눠 이동한다.
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dz)) / 0.2));
    const sx = dx / steps, sz = dz / steps, h = this.hero;
    for (let i = 0; i < steps; i++) {
      if (this.walkable(h.x + sx, h.z, HERO.radius) && !this.village.blocks(h.x + sx, h.z)) h.x += sx;
      if (this.walkable(h.x, h.z + sz, HERO.radius) && !this.village.blocks(h.x, h.z + sz)) h.z += sz;
    }
  }

  moveMob(i: number, dx: number, dz: number, radius: number): void {
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dz)) / 0.2));
    const sx = dx / steps, sz = dz / steps, m = this.mobs;
    for (let s = 0; s < steps; s++) {
      if (this.walkable(m.x[i] + sx, m.z[i], radius, -1, true)) m.x[i] += sx;
      if (this.walkable(m.x[i], m.z[i] + sz, radius, -1, true)) m.z[i] += sz;
    }
  }

  /** 순간이동: prev도 같이 옮겨 보간이 이동 경로를 그리지 않게 한다. */
  snapHero(x: number, z: number): void {
    const h = this.hero; h.x = h.prevX = x; h.z = h.prevZ = z;
  }
  snapMob(i: number, x: number, z: number): void {
    const m = this.mobs; m.x[i] = m.prevX[i] = x; m.z[i] = m.prevZ[i] = z;
  }

  /** 텔레그래프 슬롯별 출처(Ev.TelegraphFire c·d). 슬롯을 잡을 때 정한다 */
  private readonly teleSource = new Uint8Array(32);
  private readonly teleOwner = new Int16Array(32);
  allocateTelegraph(source: TeleSourceId = TeleSource.Mob, owner = -1): number {
    for (let i = 0; i < this.telegraphs.length; i++) {
      if (!this.telegraphs[i].active) { this.telegraphs[i].active = true; this.teleSource[i] = source; this.teleOwner[i] = owner; return i; }
    }
    return -1;
  }
  releaseTelegraph(index: number): void { if (index >= 0) { this.telegraphs[index].active = false; if (this.map.threatClock) this.threats.cancel(index); } }
  releaseAllTelegraphs(): void { for (let i = 0; i < this.telegraphs.length; i++) this.releaseTelegraph(i);
    this.hazards.reset(); if (this.map.threatClock) this.threats.clear(); }
  fireTelegraph(index: number): void {
    if (index < 0) return;
    const t = this.telegraphs[index], e = this.emit(Ev.TelegraphFire);
    e.x = t.x; e.z = t.z; e.a = index; e.b = t.shape; e.c = this.teleSource[index]; e.d = this.teleOwner[index];
    this.telegraphs[index].active = false;
  }
}

export function createGame(seed = 1, storage?: SaveStorage): GameSim { return new GameSim(seed, storage); }
